import * as React from 'react';
import * as styles from './styles.scss';
import { observer } from 'mobx-react-lite';
import type { SearchResultItem } from 'owa-timestream-search-orchestration';
import { DragData, Draggable } from 'owa-dnd';
import { getCanvasView, ProjectIdProvider, AssetIdProvider } from 'owa-timestream-asset-sdk';
import type { NewOrExistingAsset } from 'owa-timestream-asset-sdk-types';
import { render } from 'react-dom';
import SearchResultItemView from './SearchResultItemView';
import { getSearchableAsset } from 'owa-timestream-asset-search';
import { useGetCanvasPanZoomBounds } from './GetCanvasPanZoomBounds';
import { getSelectedProjectId } from 'owa-timestream-store';
import { Fabric } from '@fluentui/react/lib/Fabric';

export interface TimestreamSearchResultProps {
    item: SearchResultItem;
    highlightSearchTerms: boolean;
}

export default observer(function TimestreamSearchResult(props: TimestreamSearchResultProps) {
    const { item, highlightSearchTerms } = props;
    const getDragData = React.useCallback(() => {
        return createDragData(item);
    }, [item]);

    const getCanvasPanZoomBounds = useGetCanvasPanZoomBounds();
    const getDragPreview = React.useCallback(() => {
        const elem = document.createElement('div');
        elem.className = styles.assetDragView;
        elem.style.transformOrigin = '0 0 0';
        elem.style.transform = 'scale(' + getCanvasPanZoomBounds().zoom.toString() + ')';
        const { type, payload, existingAssetId } = createDragData(item)
            .itemData as NewOrExistingAsset<unknown>;
        const CanvasView = getCanvasView(type);
        render(
            <Fabric>
                <ProjectIdProvider value={getSelectedProjectId()}>
                    <AssetIdProvider value={existingAssetId}>
                        <CanvasView payload={payload} isDragPreview={true} />
                    </AssetIdProvider>
                </ProjectIdProvider>
            </Fabric>,
            elem
        );
        return elem;
    }, [item, getCanvasPanZoomBounds]);

    const content = (
        <SearchResultItemView highlightSearchTerms={highlightSearchTerms} item={item} />
    );

    return !!createDragData(item).itemData ? (
        <Draggable
            key={item.id}
            getDragData={getDragData}
            getDragPreview={getDragPreview}
            classNames={styles.draggable}>
            {content}
        </Draggable>
    ) : (
        content
    );
});

function createDragData(item: SearchResultItem): DragData {
    const searchableAsset = getSearchableAsset(item.type);
    const props = {
        item: item,
    };
    const dragAsset = searchableAsset?.createDragAsset(props);

    return {
        itemType: 'TimeStreamAsset',
        itemData: dragAsset,
        actionSource: 'ActivityPane',
    } as DragData;
}
